import { createClient } from "@supabase/supabase-js";
import { type EnvkvT } from "../../gebrauchtwagen-old/lib/vehicles-formatter";
import type { VehicleRow } from "./fetch-vehicle-data";
import type { MVRow } from "../types";

function toNum(value: string | number | null): number | null {
  if (value === null || value === undefined) return null;
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

function mapMVRowToVehicleRow(row: MVRow): VehicleRow {
  const envkv: EnvkvT = {
    required:
      row.envkv_required === null || row.envkv_required === undefined
        ? undefined
        : Boolean(row.envkv_required),
    envkvtype: row.envkv_envkvtype ?? undefined,
    emissionscombined: toNum(row.envkv_emissionscombined),
    emissionscombinedweighted: toNum(row.envkv_emissionscombinedweighted),
    consumptionlow: toNum(row.envkv_consumptionlow),
    consumptionmedium: toNum(row.envkv_consumptionmedium),
    consumptionhigh: toNum(row.envkv_consumptionhigh),
    consumptionextrahigh: toNum(row.envkv_consumptionextrahigh),
    consumptioncombined: toNum(row.envkv_consumptioncombined),
    consumptioncombinedweighted: toNum(row.envkv_consumptioncombinedweighted),
    consumptionunit: row.envkv_consumptionunit ?? null,
    electricconsumptionlow: toNum(row.envkv_electricconsumptionlow),
    electricconsumptionmedium: toNum(row.envkv_electricconsumptionmedium),
    electricconsumptionhigh: toNum(row.envkv_electricconsumptionhigh),
    electricconsumptionextrahigh: toNum(row.envkv_electricconsumptionextrahigh),
    electricconsumptioncombined: toNum(row.envkv_electricconsumptioncombined),
    electricconsumptioncombinedweighted: toNum(
      row.envkv_electricconsumptioncombinedweighted
    ),
    energycosts: toNum(row.envkv_energycosts),
    tax: toNum(row.envkv_tax),
    co2costslow: toNum(row.envkv_co2costslow),
    co2costsmedium: toNum(row.envkv_co2costsmedium),
    co2costshigh: toNum(row.envkv_co2costshigh),
    emissionsclass: row.envkv_emissionsclass ?? null,
    emissionsclassweighted: row.envkv_emissionsclassweighted ?? null,
    co2costsfromyear: row.envkv_co2costsfromyear ?? undefined,
    co2coststoyear: row.envkv_co2coststoyear ?? undefined,
    co2tarifflow: row.envkv_co2tarifflow ?? null,
    co2tariffmedium: row.envkv_co2tariffmedium ?? null,
    co2tariffhigh: row.envkv_co2tariffhigh ?? null,
    fueltariff: row.envkv_fueltariff ?? null,
    fueltariffyear: row.envkv_fueltariffyear ?? undefined,
    electricitytariff: row.envkv_electricitytariff ?? null,
    frenchemissionscombined: row.envkv_frenchemissionscombined ?? null,
    frenchemissionscombinedweighted:
      row.envkv_frenchemissionscombinedweighted ?? null,
    istaxexempt: row.envkv_istaxexempt ?? undefined,
  };

  return {
    id: row.id,
    slug: row.slug,
    make: row.make,
    model: row.model,
    vehicleid: row.vehicleid,
    type: row.type,
    body: row.body,
    fuel: row.fuel,
    transmission: row.transmission,
    localcode: row.localcode,
    color: row.color,
    power_kw: row.power_kw,
    power_hp: row.power_hp,
    customerprice: row.customerprice,
    kilometers: row.kilometers,
    sellingdealer_city: row.sellingdealer_city,
    sellingdealer_firm: row.sellingdealer_firm,
    sellingdealer_street: row.sellingdealer_street,
    sellingdealer_dealercountry: row.sellingdealer_dealercountry,
    sellingdealer_postalcode: row.sellingdealer_postalcode,
    mwst: row.mwst,
    envkv,
    thumbnail: row.thumbnail,
    images_s: row.images_s,
  };
}

export async function fetchBookmarkedVehicles(
  vehicleIds: number[]
): Promise<VehicleRow[]> {
  if (vehicleIds.length === 0) {
    return [];
  }

  const url = process.env.NEXT_PUBLIC_SUPABASE_URL;
  const anon = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;

  if (!url || !anon) {
    throw new Error("Supabase URL/Anon Key fehlen");
  }

  const supabase = createClient(url, anon, {
    auth: { autoRefreshToken: false, persistSession: false },
  });

  // Fetch only bookmarked vehicles by vehicleid
  const { data, error } = await supabase
    .from("vehicle_search_mv")
    .select("*")
    .in("vehicleid", vehicleIds);

  if (error) {
    throw new Error(error.message);
  }

  const vehicles = (data ?? []).map((row: MVRow) => mapMVRowToVehicleRow(row));

  // Maintain order of bookmarks
  return vehicleIds
    .map((id) => vehicles.find((v) => v.vehicleid === id))
    .filter((v): v is VehicleRow => v !== undefined);
}
